// ─── Route Paths ──────────────────────────────────────────────────
export const ROUTE_PATHS = {
  HOME: '/',
  ABOUT: '/about',
  ECOSYSTEM: '/ecosystem',
  COMPANIES: '/companies',
  TALENTS: '/talents',
  EVENTS: '/events',
  NEWS: '/news',
  CONTACT: '/contact',

  // Company subpages
  COMPANY: '/company/:slug',
  COMPANY_ABOUT: '/company/:slug/about',
  COMPANY_SERVICES: '/company/:slug/services',
  COMPANY_PORTFOLIO: '/company/:slug/portfolio',
  COMPANY_CONTACT: '/company/:slug/contact',
} as const;

export function companyPath(slug: string) {
  return `/company/${slug}`;
}
export function companySubPath(slug: string, sub: string) {
  return `/company/${slug}/${sub}`;
}

// ─── Company Data ─────────────────────────────────────────────────
export interface Company {
  id: string;
  slug: string;
  name: string;
  shortName: string;
  tagline: string;
  description: string;
  longDescription: string;
  accentColor: string;
  heroImage: string;
  logoInitials: string;
  services: Service[];
  stats: Stat[];
  portfolioItems: PortfolioItem[];
  contactEmail: string;
}

export interface Service {
  title: string;
  description: string;
  icon: string;
}

export interface Stat {
  value: string;
  label: string;
}

export interface PortfolioItem {
  title: string;
  category: string;
  image: string;
  year: string;
}

export interface Talent {
  id: string;
  name: string;
  title: string;
  image: string;
  company: string;
  category: string;
  achievements: string[];
}

export interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image: string;
  company: string;
}

export interface EcosystemStep {
  step: string;
  title: string;
  description: string;
  company: string;
  companySlug: string;
  icon: string;
}

// ─── Companies Registry ───────────────────────────────────────────
export const COMPANIES: Company[] = [
  {
    id: '1',
    slug: 'alv-pageant-circle',
    name: 'ALV Pageant Circle',
    shortName: 'ALV',
    tagline: 'The Premier Pageant Ecosystem',
    description: 'The flagship hub and driving force behind the ALV ecosystem — uniting talent, events, media, and global exposure under one prestige brand.',
    longDescription: `ALV Pageant Circle stands as the apex of a vertically integrated talent empire. Founded to redefine what it means to be a pageant organization, ALV operates as the strategic nucleus connecting every arm of the ecosystem — from first-stage training to world-stage competition. Our philosophy is simple: talent deserves a system built entirely around its growth.`,
    accentColor: '#c9a84c',
    heroImage: 'https://images.unsplash.com/photo-1548208394-2360ca047eb3?w=1600&q=85',
    logoInitials: 'APC',
    services: [
      { title: 'Ecosystem Strategy', description: 'Orchestrating the full talent-to-exposure pipeline across all ALV brands.', icon: 'Globe' },
      { title: 'Brand Partnerships', description: 'Exclusive global collaborations with luxury brands, media houses, and international pageant bodies.', icon: 'Handshake' },
      { title: 'Leadership Development', description: 'Executive coaching and mentoring for advocates, role models, and leaders.', icon: 'Star' },
      { title: 'Global Representation', description: 'Positioning Filipino talent on world stages — Miss Universe, Miss Grand, Miss World, and beyond.', icon: 'Award' },
    ],
    stats: [
      { value: '12+', label: 'Years of Excellence' },
      { value: '200+', label: 'Titleholders Produced' },
      { value: '30+', label: 'International Titles' },
      { value: '5', label: 'Integrated Companies' },
    ],
    portfolioItems: [
      { title: 'Miss Universe Philippines', category: 'Global Competition', image: 'https://images.unsplash.com/photo-1532328076801-a862c9d5a74a?w=800&q=80', year: '2024' },
      { title: 'ALV Grand Circle 2024', category: 'Gala Event', image: 'https://images.unsplash.com/photo-1753030768124-dba306907bba?w=800&q=80', year: '2024' },
      { title: 'Ecosystem Showcase', category: 'Brand Campaign', image: 'https://images.unsplash.com/photo-1532581646393-e75be8f3d166?w=800&q=80', year: '2023' },
      { title: 'Crown Collection', category: 'Editorial', image: 'https://images.unsplash.com/photo-1673278206746-7d874c2eb545?w=800&q=80', year: '2023' },
    ],
    contactEmail: 'hello@alvpageantcircle.com',
  },
  {
    id: '2',
    slug: 'alv-talent-circuit',
    name: 'ALV Talent Circuit',
    shortName: 'Talent Circuit',
    tagline: 'Where Talent Becomes Legacy',
    description: 'The professional management arm of the ALV ecosystem — discovering, developing, and propelling exceptional individuals into the global spotlight.',
    longDescription: `ALV Talent Circuit is the definitive talent management agency for pageant candidates, models, and advocates. We don't just manage careers — we architect them. From the earliest stages of discovery through to world-stage preparation, our team of industry veterans provides hands-on, personalized guidance for every client in our roster.`,
    accentColor: '#a89060',
    heroImage: 'https://images.unsplash.com/photo-1617182397327-a05d5c3e2abb?w=1600&q=85',
    logoInitials: 'ATC',
    services: [
      { title: 'Talent Discovery', description: 'Nationwide scouting programs to find raw, exceptional talent before anyone else.', icon: 'Search' },
      { title: 'Career Architecture', description: "Strategic career planning tailored to each individual's unique potential.", icon: 'Map' },
      { title: 'Pageant Preparation', description: 'Intensive, world-class preparation programs covering every dimension of competition.', icon: 'Crown' },
      { title: 'Brand Building', description: 'Crafting authentic personal brands that resonate globally.', icon: 'Sparkles' },
    ],
    stats: [
      { value: '500+', label: 'Talents Managed' },
      { value: '85%', label: 'Placement Rate' },
      { value: '40+', label: 'National Titles' },
      { value: '15', label: 'International Finals' },
    ],
    portfolioItems: [
      { title: 'Premiere Roster 2024', category: 'Talent Campaign', image: 'https://images.unsplash.com/photo-1769305171851-897e01aa095c?w=800&q=80', year: '2024' },
      { title: 'Training Intensive', category: 'Education', image: 'https://images.unsplash.com/photo-1660193696710-874e80d74f41?w=800&q=80', year: '2024' },
      { title: 'Global Finalists', category: 'Competition', image: 'https://images.unsplash.com/photo-1732706431172-a3e257a0657c?w=800&q=80', year: '2023' },
      { title: 'Editorial Campaign', category: 'Media', image: 'https://images.unsplash.com/photo-1537784755374-7bf81d02f58a?w=800&q=80', year: '2023' },
    ],
    contactEmail: 'talent@alvtalentcircuit.com',
  },
  {
    id: '3',
    slug: 'alv-events-international',
    name: 'ALV Events International',
    shortName: 'Entertainment',
    tagline: 'Producing Moments That Matter',
    description: 'The world-class events and production powerhouse that stages the most prestigious pageants, galas, and showcases in the region.',
    longDescription: `ALV Events International is the production force behind some of Southeast Asia's most celebrated pageant events. From intimate talent showcases to large-scale national competitions, we create immersive experiences that captivate audiences and elevate every participant. Our team of producers, directors, and creatives bring cinematic vision to live production.`,
    accentColor: '#9a7e50',
    heroImage: 'https://images.unsplash.com/photo-1698303098477-fac2428d9dfe?w=1600&q=85',
    logoInitials: 'AEI',
    services: [
      { title: 'Pageant Production', description: 'End-to-end production for national and international pageant competitions.', icon: 'Star' },
      { title: 'Gala & Events', description: 'Luxury galas, award nights, and prestige gatherings of the highest calibre.', icon: 'Ticket' },
      { title: 'Stage & Creative Direction', description: 'World-class stage design, lighting, and creative direction for unforgettable moments.', icon: 'Clapperboard' },
      { title: 'Live Broadcast', description: 'Professional multi-camera broadcast production for streaming and television.', icon: 'Tv' },
    ],
    stats: [
      { value: '150+', label: 'Events Produced' },
      { value: '50K+', label: 'Live Audience Reached' },
      { value: '25+', label: 'Production Awards' },
      { value: '12', label: 'Countries Represented' },
    ],
    portfolioItems: [
      { title: 'National Grand Finals 2024', category: 'Live Production', image: 'https://images.unsplash.com/photo-1606744038221-5e0fa1c68fe6?w=800&q=80', year: '2024' },
      { title: 'ALV Black Tie Gala', category: 'Luxury Event', image: 'https://images.unsplash.com/photo-1653821355736-0c2598d0a63e?w=800&q=80', year: '2024' },
      { title: 'Showcase International', category: 'Cultural Event', image: 'https://images.unsplash.com/photo-1644879131987-e0018885bdb3?w=800&q=80', year: '2023' },
      { title: 'Coronation Night', category: 'Pageant Production', image: 'https://images.unsplash.com/photo-1765278248762-b36b11fd8921?w=800&q=80', year: '2023' },
    ],
    contactEmail: 'events@alveventsinternational.com',
  },
  {
    id: '4',
    slug: 'sequel-advertising',
    name: 'Sequel Advertising',
    shortName: 'Sequel',
    tagline: 'Your Story, Elevated',
    description: 'The commercial and creative advertising agency that amplifies talent brands, pageant properties, and lifestyle campaigns with world-class content.',
    longDescription: `Sequel Advertising is the creative engine fueling the ALV ecosystem's visual identity. Specializing in talent-driven campaigns, luxury lifestyle content, and premium brand storytelling, Sequel bridges the world of pageantry and commercial media. Our campaigns don't just sell — they captivate, inspire, and endure.`,
    accentColor: '#b89040',
    heroImage: 'https://images.unsplash.com/photo-1763347718796-0824c286c77b?w=1600&q=85',
    logoInitials: 'SQ',
    services: [
      { title: 'Campaign Strategy', description: 'Integrated marketing campaigns that command attention across all platforms.', icon: 'Target' },
      { title: 'Content Production', description: 'Editorial shoots, video production, and cinematic brand content.', icon: 'Camera' },
      { title: 'Digital & Social', description: 'Premium social media strategy and content creation for talent and brands.', icon: 'TrendingUp' },
      { title: 'Brand Identity', description: 'Complete visual identity systems for emerging talents and established brands.', icon: 'Palette' },
    ],
    stats: [
      { value: '300+', label: 'Campaigns Executed' },
      { value: '50+', label: 'Brand Partners' },
      { value: '10M+', label: 'Impressions Generated' },
      { value: '8', label: 'Creative Awards' },
    ],
    portfolioItems: [
      { title: 'Crown & Camera 2024', category: 'Brand Campaign', image: 'https://images.unsplash.com/photo-1642081623992-12a24dd86060?w=800&q=80', year: '2024' },
      { title: 'Luxe Editorial Series', category: 'Content', image: 'https://images.unsplash.com/photo-1532581646393-e75be8f3d166?w=800&q=80', year: '2024' },
      { title: 'Talent Spotlight', category: 'Digital Campaign', image: 'https://images.unsplash.com/photo-1663431512803-9ca20258d1f5?w=800&q=80', year: '2023' },
      { title: 'Brand Awakening', category: 'Identity', image: 'https://images.unsplash.com/photo-1729767559071-32ad5d1e3534?w=800&q=80', year: '2023' },
    ],
    contactEmail: 'creative@sequeladvertising.com',
  },
  {
    id: '5',
    slug: 'miss-world-philippines',
    name: 'Miss World Philippines',
    shortName: 'Pageants',
    tagline: 'Beauty with a Purpose',
    description: 'The official national pageant platform representing the Philippines at Miss World — celebrating intelligence, talent, and advocacy on the global stage.',
    longDescription: `Miss World Philippines is one of the most prestigious and purposeful pageant platforms in the country. Partnering with the global Miss World Organization, we seek and crown extraordinary women who embody the "Beauty with a Purpose" ethos — women who are not just beautiful, but brilliant, driven, and committed to making a difference in the world.`,
    accentColor: '#d4a520',
    heroImage: 'https://images.unsplash.com/photo-1706564374952-fb2206554e9c?w=1600&q=85',
    logoInitials: 'MWP',
    services: [
      { title: 'National Competition', description: 'The official search for the Philippines representative to the Miss World international pageant.', icon: 'Globe' },
      { title: 'Advocacy Programs', description: 'Championing Beauty with a Purpose through community outreach and social impact projects.', icon: 'Heart' },
      { title: 'Titleholder Support', description: 'Comprehensive support, training, and management for national and international titleholders.', icon: 'Crown' },
      { title: 'International Prep', description: 'World-stage preparation and coaching for Miss World competition success.', icon: 'Award' },
    ],
    stats: [
      { value: '10+', label: 'Years as Official Org' },
      { value: '5', label: 'Miss World Placements' },
      { value: '100+', label: 'Provincial Queens' },
      { value: '3', label: 'Top 5 Finishes' },
    ],
    portfolioItems: [
      { title: 'Miss World Philippines 2024', category: 'National Finals', image: 'https://images.unsplash.com/photo-1744973148696-9a57300bcea6?w=800&q=80', year: '2024' },
      { title: 'Beauty with Purpose Gala', category: 'Advocacy', image: 'https://images.unsplash.com/photo-1665615835680-bc3ddd529856?w=800&q=80', year: '2024' },
      { title: 'Miss World 2023 Representation', category: 'International', image: 'https://images.unsplash.com/photo-1671541830281-1f2e4d10dfb1?w=800&q=80', year: '2023' },
      { title: 'Provincial Tour', category: 'Search', image: 'https://images.unsplash.com/photo-1624427537426-21dd3652d797?w=800&q=80', year: '2023' },
    ],
    contactEmail: 'info@missworldphilippines.org',
  },
];

export function getCompanyBySlug(slug: string): Company | undefined {
  return COMPANIES.find(c => c.slug === slug);
}

// ─── Talents ──────────────────────────────────────────────────────
export const TALENTS: Talent[] = [
  {
    id: '1',
    name: 'Sofia Reyes',
    title: 'Miss World Philippines 2024',
    image: 'https://images.unsplash.com/photo-1548208394-2360ca047eb3?w=600&q=80',
    company: 'Miss World Philippines',
    category: 'Pageant',
    achievements: ['Miss World Philippines 2024', 'Top 10 Miss World International', 'UN Youth Ambassador'],
  },
  {
    id: '2',
    name: 'Isabella Cruz',
    title: 'Brand Ambassador & Model',
    image: 'https://images.unsplash.com/photo-1769305171851-897e01aa095c?w=600&q=80',
    company: 'ALV Talent Circuit',
    category: 'Model',
    achievements: ['Vogue Philippines Feature', 'L\'Oréal Brand Ambassador', '3x National Champion'],
  },
  {
    id: '3',
    name: 'Camille Santos',
    title: 'National Queen & Advocate',
    image: 'https://images.unsplash.com/photo-1660193696710-874e80d74f41?w=600&q=80',
    company: 'ALV Talent Circuit',
    category: 'Pageant',
    achievements: ['Grand International Finalist', 'UNICEF Advocate', 'TEDx Speaker'],
  },
  {
    id: '4',
    name: 'Andrea Lim',
    title: 'Commercial Talent & Host',
    image: 'https://images.unsplash.com/photo-1617182397327-a05d5c3e2abb?w=600&q=80',
    company: 'Sequel Advertising',
    category: 'Commercial',
    achievements: ['50+ Brand Campaigns', 'Regional TV Host', 'Forbes 30 Under 30'],
  },
  {
    id: '5',
    name: 'Mariana Dela Rosa',
    title: 'International Finalist',
    image: 'https://images.unsplash.com/photo-1729767559071-32ad5d1e3534?w=600&q=80',
    company: 'Miss World Philippines',
    category: 'Pageant',
    achievements: ['Miss Grand International Top 5', 'Cultural Ambassador', 'Author'],
  },
  {
    id: '6',
    name: 'Natasha Villanueva',
    title: 'Editorial & Runway Model',
    image: 'https://images.unsplash.com/photo-1532581646393-e75be8f3d166?w=600&q=80',
    company: 'ALV Talent Circuit',
    category: 'Model',
    achievements: ['Paris Fashion Week', 'Harper\'s Bazaar Cover', 'NYFW Featured'],
  },
  {
    id: '7',
    name: 'Diana Morales',
    title: 'Pageant Coach & Director',
    image: 'https://images.unsplash.com/photo-1663431512803-9ca20258d1f5?w=600&q=80',
    company: 'ALV Pageant Circle',
    category: 'Director',
    achievements: ['100+ Queens Coached', 'Miss Universe Prep Expert', 'Author of "Crown Ready"'],
  },
  {
    id: '8',
    name: 'Bianca Torres',
    title: 'Brand & Content Creator',
    image: 'https://images.unsplash.com/photo-1642081623992-12a24dd86060?w=600&q=80',
    company: 'Sequel Advertising',
    category: 'Commercial',
    achievements: ['2M+ Social Following', '30 Brand Partnerships', 'Digital Creator Award'],
  },
];

// ─── News ─────────────────────────────────────────────────────────
export const NEWS: NewsItem[] = [
  {
    id: '1',
    title: 'Miss World Philippines 2025 Search: Applications Now Open',
    excerpt: 'The highly anticipated search for the next Miss World Philippines representative has officially begun. Applications are open to all qualified Filipino women nationwide.',
    date: 'April 15, 2026',
    category: 'Pageant',
    image: 'https://images.unsplash.com/photo-1706564374952-fb2206554e9c?w=800&q=80',
    company: 'Miss World Philippines',
  },
  {
    id: '2',
    title: 'ALV Grand Circle Gala 2026 Announced for September',
    excerpt: 'ALV Events International announces the most ambitious gala production in ALV history — a two-night celebration of talent, artistry, and global achievement.',
    date: 'April 10, 2026',
    category: 'Event',
    image: 'https://images.unsplash.com/photo-1753030768124-dba306907bba?w=800&q=80',
    company: 'ALV Events International',
  },
  {
    id: '3',
    title: 'Sequel Advertising Wins Regional Creative Excellence Award',
    excerpt: 'For the third consecutive year, Sequel Advertising has been recognized for outstanding creative work in the talent and lifestyle advertising sector.',
    date: 'March 28, 2026',
    category: 'Award',
    image: 'https://images.unsplash.com/photo-1532581646393-e75be8f3d166?w=800&q=80',
    company: 'Sequel Advertising',
  },
  {
    id: '4',
    title: 'ALV Talent Circuit Opens New Flagship Training Academy in Manila',
    excerpt: 'State-of-the-art training facilities open their doors in BGC, Taguig — equipped with runway studios, coaching suites, and media production stages.',
    date: 'March 15, 2026',
    category: 'Announcement',
    image: 'https://images.unsplash.com/photo-1698303098477-fac2428d9dfe?w=800&q=80',
    company: 'ALV Talent Circuit',
  },
  {
    id: '5',
    title: 'ALV Pageant Circle Signs Global Partnership with International Pageant Federation',
    excerpt: 'A landmark agreement cements ALV\'s position as a globally recognized talent organization, opening new pathways for Philippine representatives on the world stage.',
    date: 'February 20, 2026',
    category: 'Partnership',
    image: 'https://images.unsplash.com/photo-1744973148696-9a57300bcea6?w=800&q=80',
    company: 'ALV Pageant Circle',
  },
  {
    id: '6',
    title: 'Sofia Reyes Places Top 10 at Miss World International in London',
    excerpt: 'Miss World Philippines 2024 Sofia Reyes made history with a stunning Top 10 finish at the Miss World international pageant, becoming the highest-placing Filipino in a decade.',
    date: 'January 30, 2026',
    category: 'Achievement',
    image: 'https://images.unsplash.com/photo-1548208394-2360ca047eb3?w=800&q=80',
    company: 'Miss World Philippines',
  },
];

// ─── Ecosystem Steps ──────────────────────────────────────────────
export const ECOSYSTEM_STEPS: EcosystemStep[] = [
  {
    step: '01',
    title: 'Training & Discovery',
    description: 'Every journey begins with discovery. ALV Talent Circuit identifies raw potential through nationwide scouting and intensive training programs that polish raw talent into world-ready individuals.',
    company: 'ALV Talent Circuit',
    companySlug: 'alv-talent-circuit',
    icon: 'Sparkles',
  },
  {
    step: '02',
    title: 'Management & Development',
    description: 'Once identified, talent enters the full ALV management system — career architecture, personal branding, and strategic placement managed by industry veterans.',
    company: 'ALV Talent Circuit',
    companySlug: 'alv-talent-circuit',
    icon: 'Users',
  },
  {
    step: '03',
    title: 'Commercial Monetization',
    description: 'Sequel Advertising activates talent commercially — campaigns, brand partnerships, editorial content, and digital media that convert talent into iconic personal brands.',
    company: 'Sequel Advertising',
    companySlug: 'sequel-advertising',
    icon: 'TrendingUp',
  },
  {
    step: '04',
    title: 'Event Showcase',
    description: 'ALV Events International creates world-class stages — national competitions, galas, and showcases that give talent its defining moments in front of global audiences.',
    company: 'ALV Events International',
    companySlug: 'alv-events-international',
    icon: 'Star',
  },
  {
    step: '05',
    title: 'Global Exposure',
    description: 'The pinnacle of the pipeline — Miss World Philippines and ALV Pageant Circle\'s international partnerships propel the finest talent onto the world\'s most prestigious stages.',
    company: 'Miss World Philippines',
    companySlug: 'miss-world-philippines',
    icon: 'Globe',
  },
];
